'use client';
import Link from 'next/link';
import { FC } from 'react';
import { motion } from 'framer-motion';
import { accountLinkType } from '@creator-suite/data';

type SocialMediaType = (typeof accountLinkType.enumValues)[number];

const socialIcons: Partial<Record<SocialMediaType, React.ReactNode>> = {
  spotify: (
    <svg
      className="opacity-80"
      xmlns="http://www.w3.org/2000/svg"
      width="33"
      height="33"
      fill="none"
      viewBox="0 0 33 33"
    >
      <g clipPath="url(#clip0_53_437)">
        <path
          fill="currentColor"
          d="M16.5.388C7.738.388.628 7.498.628 16.26S7.738 32.132 16.5 32.132s15.872-7.11 15.872-15.872C32.372 7.5 25.262.388 16.5.388zm6.445 23.354c-.27 0-.436-.083-.685-.23-3.994-2.407-8.64-2.51-13.229-1.569-.25.064-.576.167-.761.167-.621 0-1.012-.493-1.012-1.011 0-.66.39-.973.87-1.076 5.242-1.158 10.6-1.056 15.169 1.677.39.25.62.474.62 1.056a.967.967 0 01-.972.986zm1.721-4.199c-.332 0-.556-.147-.787-.268-4-2.368-9.965-3.322-15.27-1.882-.307.083-.474.166-.762.166a1.243 1.243 0 01-1.241-1.241c0-.685.332-1.14.992-1.325 1.779-.5 3.596-.87 6.259-.87 4.153 0 8.166 1.03 11.328 2.912.518.307.723.704.723 1.26a1.24 1.24 0 01-1.242 1.248zm1.984-4.876c-.332 0-.537-.084-.825-.25-4.557-2.72-12.704-3.373-17.978-1.9-.23.063-.518.166-.825.166-.845 0-1.492-.66-1.492-1.51 0-.871.538-1.364 1.114-1.53 2.253-.66 4.774-.973 7.52-.973 4.672 0 9.568.973 13.146 3.059.499.288.825.685.825 1.446 0 .87-.704 1.492-1.485 1.492z"
        ></path>
      </g>
      <defs>
        <clipPath id="clip0_53_437">
          <path
            fill="currentColor"
            d="M0 0H32V32H0z"
            transform="translate(.5 .26)"
          ></path>
        </clipPath>
      </defs>
    </svg>
  ),
  instagram: (
    <svg
      className="opacity-80"
      xmlns="http://www.w3.org/2000/svg"
      width="33"
      height="33"
      fill="none"
      viewBox="0 0 33 33"
    >
      <g clipPath="url(#clip0_53_439)">
        <path
          fill="currentColor"
          d="M16.954 8.829a7.863 7.863 0 00-7.874 7.876 7.863 7.863 0 007.874 7.876 7.863 7.863 0 007.875-7.876 7.863 7.863 0 00-7.875-7.876zm0 12.996a5.13 5.13 0 01-5.12-5.12 5.125 5.125 0 015.12-5.12 5.125 5.125 0 015.12 5.12 5.13 5.13 0 01-5.12 5.12zM26.988 8.506a1.833 1.833 0 01-1.837 1.837 1.837 1.837 0 111.837-1.837zm5.215 1.865c-.117-2.461-.679-4.64-2.481-6.437-1.796-1.796-3.975-2.358-6.435-2.481-2.536-.144-10.136-.144-12.672 0-2.454.116-4.633.678-6.435 2.474-1.803 1.796-2.358 3.976-2.481 6.437-.144 2.536-.144 10.138 0 12.675.116 2.46.678 4.64 2.48 6.436 1.803 1.796 3.976 2.358 6.436 2.482 2.536.144 10.136.144 12.672 0 2.46-.117 4.64-.679 6.435-2.482 1.795-1.796 2.358-3.976 2.48-6.436.145-2.537.145-10.132 0-12.668zM28.927 25.76a5.184 5.184 0 01-2.92 2.92c-2.021.802-6.819.617-9.053.617-2.234 0-7.038.178-9.053-.617a5.183 5.183 0 01-2.92-2.92c-.801-2.022-.616-6.82-.616-9.055 0-2.235-.178-7.04.616-9.056a5.184 5.184 0 012.92-2.92c2.022-.802 6.82-.617 9.053-.617 2.234 0 7.039-.178 9.053.617a5.184 5.184 0 012.92 2.92c.802 2.023.617 6.821.617 9.056 0 2.234.185 7.04-.617 9.055z"
        ></path>
      </g>
      <defs>
        <clipPath id="clip0_53_439">
          <path
            fill="currentColor"
            d="M0 0H32V32H0z"
            transform="translate(.5 .26)"
          ></path>
        </clipPath>
      </defs>
    </svg>
  ),
  apple_music: (
    <svg
      className="opacity-80"
      xmlns="http://www.w3.org/2000/svg"
      width="33"
      height="33"
      fill="none"
      viewBox="0 0 33 33"
    >
      <path
        fill="currentColor"
        d="M25.468 17.17c-.015-2.621 1.162-4.6 3.544-6.058-1.333-1.922-3.346-2.979-6.004-3.186-2.517-.2-5.267 1.479-6.274 1.479-1.063 0-3.502-1.408-5.416-1.408-3.955.065-8.159 3.18-8.159 9.516 0 1.872.34 3.806 1.021 5.801.907 2.622 4.182 9.052 7.6 8.945 1.786-.043 3.047-1.279 5.372-1.279 2.255 0 3.424 1.279 5.416 1.279 3.445-.05 6.409-5.894 7.273-8.523-4.622-2.193-4.373-6.43-4.373-6.566zM21.455 5.44c1.936-2.315 1.758-4.422 1.702-5.18-1.709.1-3.687 1.172-4.814 2.494-1.24 1.414-1.97 3.164-1.814 5.136 1.85.143 3.537-.814 4.926-2.45z"
      ></path>
    </svg>
  ),
  youtube: (
    <svg
      className="opacity-80"
      xmlns="http://www.w3.org/2000/svg"
      width="33"
      height="33"
      fill="none"
      viewBox="0 0 33 33"
    >
      <g clipPath="url(#clip0_53_443)">
        <path
          fill="currentColor"
          d="M31.831 8.53a4.02 4.02 0 00-2.829-2.847C26.507 5.01 16.5 5.01 16.5 5.01s-10.007 0-12.502.673a4.02 4.02 0 00-2.83 2.848C.5 11.042.5 16.283.5 16.283s0 5.24.669 7.752c.368 1.386 1.452 2.432 2.829 2.802 2.495.673 12.502.673 12.502.673s10.007 0 12.502-.673c1.377-.37 2.461-1.416 2.83-2.802.668-2.512.668-7.752.668-7.752s0-5.24-.669-7.752zM13.227 21.042v-9.516l8.364 4.758-8.364 4.758z"
        ></path>
      </g>
      <defs>
        <clipPath id="clip0_53_443">
          <path
            fill="currentColor"
            d="M0 0H32V32H0z"
            transform="translate(.5 .26)"
          ></path>
        </clipPath>
      </defs>
    </svg>
  ),
  tiktok: (
    <svg
      className="opacity-80"
      xmlns="http://www.w3.org/2000/svg"
      width="33"
      height="33"
      fill="none"
      viewBox="0 0 33 33"
    >
      <path
        fill="currentColor"
        d="M13.13 25.076c2.238 0 4.052-1.602 4.052-4.052V2.85h4.86c-.273 3.314 3.022 6.516 6.655 6.443v4.57c-3.107 0-5.75-1.315-6.67-2.044v9.205c0 4.052-3.187 8.647-8.897 8.647-5.71 0-8.828-4.595-8.828-8.647 0-5.72 6.052-9.036 10.06-8.226v4.655a4.264 4.264 0 00-1.164-.177c-2.257-.083-4.12 1.747-4.12 3.748a4.052 4.052 0 004.052 4.052z"
      ></path>
    </svg>
  ),
};

export type SocialButtonProps = {
  url: string;
  icon: SocialMediaType;
};

export const SocialButton: FC<SocialButtonProps> = ({ url, icon }) => {
  return (
    <Link
      href={url}
      className=""
      target="_blank"
      aria-label={`Gehe zu ${icon}`}
    >
      {socialIcons[icon]}
    </Link>
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 1, // delay of 1000ms
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { x: -10, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
};

export const SocialIcons = ({
  profiles = [],
  primaryColor,
}: {
  profiles: SocialButtonProps[];
  primaryColor: string;
}) => {
  return (
    <motion.div
      className="mx-auto flex items-center space-x-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {profiles.map((link, index) => (
        <motion.div
          key={index}
          className="flex items-center space-x-2"
          style={{ color: primaryColor }}
          variants={itemVariants}
        >
          <SocialButton icon={link.icon} url={link.url} />
        </motion.div>
      ))}
    </motion.div>
  );
};
